import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import gql from 'graphql-tag'
import { useMutation, useQuery } from 'react-apollo'

import TopicDetail from 'src/pages/TopicDetail/TopicDetail'
import TopicDetailPlaceholder from 'src/pages/TopicDetail/TopicDetailPlaceholder'
import DataFetchError from 'src/ui/DataFetchError/DataFetchError'
import { t } from 'src/lib/i18n-react'

export const TOPIC_DETAIL = gql`
  query TOPIC_DETAIL($topicId: ID!) {
    level(levelId: $topicId) {
      id
      name
      description
      coverImageUrl
      units {
        id
        name
        iconUrl
        progress {
          isQueued
          isCompleted
          isCurrent
          percentOfExercisesSeen
        }
      }
    }
  }
`

const APPEND_TOPIC_TO_QUEUE = gql`
  mutation APPEND_TOPIC_TO_QUEUE($topicInput: LevelInputType!) {
    response: appendTopicToQueue(topic: $topicInput) {
      errors {
        code
        message
      }
    }
  }
`

const REMOVE_TOPIC_FROM_QUEUE = gql`
  mutation REMOVE_TOPIC_FROM_QUEUE($topicInput: LevelInputType!) {
    response: removeTopicFromQueue(topic: $topicInput) {
      status
      errors {
        code
        message
      }
    }
  }
`

const TopicDetailLazy = (props) => {
  const { topicId, onShowSnackbar } = props
  const [inCourseValue, setInCourseValue] = useState(false)
  const [unitsInCourseValuesMap, setUnitsInCourseValues] = useState({})
  const [appendTopicToQueue, { loading: appendMutationLoading }] = useMutation(
    APPEND_TOPIC_TO_QUEUE
  )
  const [
    removeTopicFromQueue,
    { loading: removeMutationLoading },
  ] = useMutation(REMOVE_TOPIC_FROM_QUEUE)
  const response = useQuery(TOPIC_DETAIL, {
    variables: {
      topicId,
    },
    fetchPolicy: 'network-only',
  })

  const { data, loading } = response
  const topic = data?.level
  const isMutationLoading = appendMutationLoading || removeMutationLoading

  useEffect(() => {
    if (topic?.units?.length) {
      setInCourseValue(topic.units.every((u) => u.progress?.isQueued))
      setUnitsInCourseValues(
        topic.units.reduce((map, u) => {
          map[u.id] = u.progress?.isQueued
          return map
        }, {})
      )
    }
  }, [topic, setInCourseValue])

  const handleMutationError = (newValue) => {
    onShowSnackbar({
      message: newValue
        ? t('Error while adding topic to course. Please try again later.')
        : t('Error while removing topic from course. Please try again later.'),
      type: 'error',
    })
  }

  const handleInCourseValueChanged = (newValue) => {
    setInCourseValue(newValue)
    setUnitsInCourseValues(
      topic.units.reduce((map, u) => {
        map[u.id] = newValue
        return map
      }, {})
    )
  }

  const handleToggleInCourse = async (newValue) => {
    if (isMutationLoading) {
      return
    }

    try {
      const topicInput = { id: topic.id }
      const options = {
        variables: { topicInput },
      }
      let mutationResponse
      if (newValue) {
        mutationResponse = await appendTopicToQueue(options)
      } else {
        mutationResponse = await removeTopicFromQueue(options)
      }

      if (
        !mutationResponse.data?.response ||
        mutationResponse.data?.response?.errors?.length
      ) {
        handleMutationError(newValue)
      } else {
        onShowSnackbar({
          message: newValue
            ? t('Topic successfully added to your course!')
            : t('Topic removed from your course.'),
          type: 'info',
        })
        handleInCourseValueChanged(newValue)
      }
    } catch (error) {
      console.log(error)
      handleMutationError(newValue)
    }
  }

  if (loading) {
    return <TopicDetailPlaceholder />
  }

  if (topic) {
    return (
      <TopicDetail
        topic={topic}
        units={topic.units}
        inCourse={inCourseValue}
        unitsInCourseValuesMap={unitsInCourseValuesMap}
        onToggleInCourse={handleToggleInCourse}
      />
    )
  }

  return <DataFetchError graphQLResponse={response} />
}

TopicDetailLazy.propTypes = {
  topicId: PropTypes.string.isRequired,
  onShowSnackbar: PropTypes.func.isRequired,
}

export default TopicDetailLazy
